import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiSearch } from "react-icons/fi";
import axios from "axios";
import apiKey from "../../../config/apiKey";
import { toast } from "react-toastify";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import QRCodeDisplay from "../../../components/QRCodeDisplay";
import { Link } from "react-router-dom";
import PayNow from "../FindTeacher/PayNow";

function OnlineTutoring() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const student = JSON.parse(localStorage.getItem("edo-student"));
  const userId = student ? student.student.id : "";
  const [showThirdModal, setShowThirdModal] = useState(false);
  const [specificLink, setSpecificLink] = useState("");
  const [password, setPassword] = useState("");
  const [meetingNum, setMeetingNum] = useState("");
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("Newest");
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const handleShowQRCodeModal = (classId) => {
    setSelectedClassId(classId);
    setShowQRCodeModal(true);
  };

  const handleCloseQRCodeModal = () => {
    setShowQRCodeModal(false);
    setSelectedClassId(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${apiKey}/api/edolutions/student/read-notifications-all/${userId}`,
          { headers: { Authorization: `Bearer ${student?.token}` } }
        );
        setData(res.data.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchData();
  }, [userId, student?.token]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (e) => setSortOption(e.target.value);

  const sortedData = [...data].sort((a, b) =>
    sortOption === "Newest"
      ? new Date(b.created_at) - new Date(a.created_at)
      : new Date(a.created_at) - new Date(b.created_at)
  );

  const filteredData = sortedData.filter(
    (item) =>
      item.teacher_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.class_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subjects.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handlePageChange = (p) =>
    setCurrentPage(Math.min(Math.max(1, p), totalPages));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(p + 1, totalPages));
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // ✅ Safe MeetingLink with checks
  const MeetingLink = (link) => {
    if (!link) {
      toast.error(t("studentDash.myTutoring.meetingLinkNotAvailable"), {
        position: "bottom-right",
        theme: "colored",
      });
      return;
    }

    try {
      setSpecificLink(link);
      const urlObj = new URL(link);
      setPassword(urlObj.searchParams.get("pwd") || "");
      setMeetingNum(link.match(/\/j\/(\d+)/)?.[1] || "");
      setShowThirdModal(true);
    } catch (err) {
      console.error("Invalid meeting URL:", err);
      toast.error(t("studentDash.myTutoring.meetingLinkInvalid"), {
        position: "bottom-right",
        theme: "colored",
      });
    }
  };

  const [detail, setDetailModal] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [studentId, setStudentId] = useState(null);

  const studentDetails = async (id) => {
    setStudentId(id);
    try {
      const response = await axios.get(
        `${apiKey}/api/edolutions/create-class-home/${id}`
      );
      const sData = response.data.data;
      if (!sData) {
        toast.error(t("studentDash.myTutoring.studentRecordNotFound"), { theme: "colored" });
        return;
      }
      setStudentData(sData);
      setDetailModal(true);
    } catch (error) {
      toast.error(t("studentDash.myTutoring.studentRecordNotFoundCreateClass"), {
        theme: "colored",
      });
    }
  };

  const handleImageClick = (url) => setSelectedImage(url);
  const closeImageModal = () => setSelectedImage(null);

  function formatTime(time) {
    let [h, m] = time.split(":");
    let period = "AM";
    h = parseInt(h, 10);
    if (h >= 12) {
      period = "PM";
      if (h > 12) h -= 12;
    } else if (h === 0) {
      h = 12;
    }
    return `${h}:${m} ${period}`;
  }

  // ✅ New helper for scheduled_at
  function formatDateTime(dt) {
    if (!dt) return t("studentDash.myTutoring.notScheduledYet");

    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return dt; // fallback raw string

    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  const handleToggleDetails = (index) =>
    setExpandedRow(expandedRow === index ? null : index);

  return (
    <>
      <div className="flex items-center xl:p-5 sm:p-0 justify-center">
        <div className="w-full rounded-3xl shadow-xl p-5 bg-white border border-gray-100">
          {/* Header Row */}
          <div className="mt-2">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 p-2">
              <div className="w-full md:w-auto">
                <h2 className="text-[22px] font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]">
                  {t("studentDash.myTutoring.mySessions")}
                </h2>
                <p className="text-xs text-gray-500">
                  {t("studentDash.myTutoring.subtitle")}
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-3 md:items-center w-full md:w-auto">
                {/* Search */}
                <div className="relative rounded-lg flex items-center px-4 py-2 border bg-white shadow-sm">
                  <FiSearch className="absolute left-3 h-5 w-5 text-[#2A3A54]" />
                  <input
                    type="text"
                    className="pl-7 pr-2 py-1 outline-none text-sm w-64"
                    placeholder={t("studentDash.myTutoring.searchPlaceholder")}
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>

                {/* Sort */}
                <div className="relative">
                  <select
                    className="cursor-pointer px-4 py-2 text-sm rounded-lg border bg-white shadow-sm"
                    value={sortOption}
                    onChange={handleSortChange}
                  >
                    <option value="Newest">{t("studentDash.myTutoring.sortNewest")}</option>
                    <option value="Oldest">{t("studentDash.myTutoring.sortOldest")}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Table (Desktop) */}
            <div className="mt-5 overflow-x-auto">
              <table className="hidden sm:table min-w-full text-sm text-left text-gray-600 table-auto">
                <thead className="text-xs sm:text-sm font-semibold text-white">
                  <tr className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]">
                    <th className="py-3 px-3">{t("studentDash.myTutoring.colMentorName")}</th>
                    <th className="py-3 px-3">{t("studentDash.myTutoring.colProfession")}</th>
                    <th className="py-3 px-3">{t("studentDash.myTutoring.colSpecialization")}</th>
                    <th className="py-3 px-3">{t("studentDash.myTutoring.colType")}</th>
                    <th className="py-3 px-3">{t("studentDash.myTutoring.colFee")}</th>
                    <th className="py-3 px-3">{t("studentDash.myTutoring.colMeetingDetails")}</th>
                  </tr>
                </thead>

                <tbody className="text-black text-xs sm:text-sm font-medium">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="py-10 text-center">
                        <div className="inline-flex flex-col items-center">
                          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-transparent animate-spin" />
                          <p className="mt-3 text-sm font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]">
                            {t("studentDash.common.loadingEllipsis")}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-gray-500">
                        {t("studentDash.myTutoring.noSessionsAvailable")}
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((item, index) => (
                      <React.Fragment key={index}>
                        <tr className="border-b last:border-0">
                          <td className="py-4 px-3">{item.teacher_name}</td>
                          <td className="py-4 px-3">{item.class_name}</td>
                          <td className="py-4 px-3">{item.subjects}</td>
                          <td className="py-4 px-3 capitalize">
                            {item.type_name}
                          </td>

                          <td className="py-4 px-3">
                            {item.payment === "0" ? (
                              // Free demo – no payment needed
                              <span className="inline-block text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                {t("studentDash.myTutoring.freeDemo")}
                              </span>
                            ) : item.Fee === "unpaid" ? (
                              // Unpaid – show PayNow
                              <PayNow
                                classId={item.tbl_id}
                                stdId={item.student_id}
                                item={item.subjects}
                                amount={item.payment}
                                payeeName={item.student_name}
                                email={item.email}
                                msisdn={item.phone}
                                description="Tuition Fee"
                              />
                            ) : (
                              // Paid
                              <span className="inline-block text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                                {t("studentDash.myTutoring.paid")}
                              </span>
                            )}
                          </td>

                          <td className="py-4 px-3">
                            <div className="flex items-center gap-2">
                              {item.type_name === "online" ? (
                                <button
                                  onClick={() => MeetingLink(item.join_url)}
                                  disabled={!item.join_url}
                                  className={`text-white text-xs px-3 py-1 rounded-md bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] ${
                                    !item.join_url
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                >
                                  {item.join_url ? t("studentDash.myTutoring.openLink") : t("studentDash.myTutoring.noLink")}
                                </button>
                              ) : (
                                <button
                                  onClick={() => studentDetails(item.tbl_id)}
                                  className="text-white text-xs px-3 py-1 rounded-md bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"
                                >
                                  {t("studentDash.common.view")}
                                </button>
                              )}

                              <button
                                className="text-[#2A3A54] hover:opacity-80"
                                onClick={() => handleToggleDetails(index)}
                                title={t("studentDash.myTutoring.toggleDetails")}
                              >
                                {expandedRow === index ? (
                                  <AiOutlineEyeInvisible className="text-lg" />
                                ) : (
                                  <AiOutlineEye className="text-lg" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {expandedRow === index && (
                          <tr>
                            <td colSpan="6" className="px-3 pb-4">
                              <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
                                <div className="px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]">
                                  {t("studentDash.myTutoring.sessionDetails")}
                                </div>
                                <div className="p-3">
                                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-xs sm:text-sm">
                                    <div>
                                      <span className="text-gray-500">{t("studentDash.myTutoring.dayLabel")}</span>{" "}
                                      <span className="capitalize">
                                        {item.day_name || t("studentDash.myTutoring.notDefined")}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">
                                        {t("studentDash.myTutoring.timingLabel")}
                                      </span>{" "}
                                      {item.start_time && item.end_time
                                        ? `${formatTime(
                                            item.start_time
                                          )} to ${formatTime(item.end_time)}`
                                        : item.timeslot_id}
                                    </div>
                                    <div>
                                      <span className="text-gray-500">
                                        {t("studentDash.myTutoring.durationLabel")}
                                      </span>{" "}
                                      {item.days} {item.duration}
                                    </div>
                                    <div>
                                      <span className="text-gray-500">
                                        {t("studentDash.myTutoring.paymentLabel")}
                                      </span>{" "}
                                      Rs.{item.payment}
                                    </div>
                                    <div>
                                      <span className="text-gray-500">
                                        {t("studentDash.myTutoring.scheduledAtLabel")}
                                      </span>{" "}
                                      {formatDateTime(item.scheduled_at)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>

              {/* Cards (Mobile) */}
              <div className="sm:hidden">
                {loading ? (
                  <div className="py-12 text-center">
                    <div className="inline-flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-transparent animate-spin" />
                      <p className="mt-3 text-sm font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]">
                        {t("studentDash.common.loadingEllipsis")}
                      </p>
                    </div>
                  </div>
                ) : paginatedData.length === 0 ? (
                  <p className="text-gray-500 text-center py-5">
                    {t("studentDash.myTutoring.noSessionsAvailable")}
                  </p>
                ) : (
                  paginatedData.map((item, index) => (
                    <div
                      key={index}
                      className="border p-3 bg-white shadow-sm rounded-xl mb-4"
                    >
                      <div className="flex justify-between text-sm">
                        <p className="font-semibold">{t("studentDash.myTutoring.mentor")}</p>
                        <p>{item.teacher_name}</p>
                      </div>
                      <div className="flex justify-between text-sm">
                        <p className="font-semibold">{t("studentDash.myTutoring.colProfession")}</p>
                        <p>{item.class_name}</p>
                      </div>
                      <div className="flex justify-between text-sm">
                        <p className="font-semibold">{t("studentDash.myTutoring.colSpecialization")}</p>
                        <p>{item.subjects}</p>
                      </div>
                      <div className="flex justify-between text-sm">
                        <p className="font-semibold">{t("studentDash.myTutoring.colType")}</p>
                        <p className="capitalize">{item.type_name}</p>
                      </div>

                      <div className="mt-2 flex items-center justify-between">
                        {item.type_name === "online" ? (
                          <button
                            onClick={() => MeetingLink(item.join_url)}
                            disabled={!item.join_url}
                            className={`text-white text-xs px-3 py-1 rounded-md bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] ${
                              !item.join_url
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            {item.join_url ? t("studentDash.myTutoring.openLink") : t("studentDash.myTutoring.noLink")}
                          </button>
                        ) : (
                          <button
                            onClick={() => studentDetails(item.tbl_id)}
                            className="text-white text-xs px-3 py-1 rounded-md bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"
                          >
                            {t("studentDash.common.view")}
                          </button>
                        )}

                        {item.payment === "0" ? (
                          // Free demo – no payment needed
                          <span className="inline-block text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                            {t("studentDash.myTutoring.freeDemo")}
                          </span>
                        ) : item.Fee === "unpaid" ? (
                          // Unpaid – show PayNow
                          <PayNow
                            classId={item.tbl_id}
                            stdId={item.student_id}
                            item={item.subjects}
                            amount={item.payment}
                            payeeName={item.student_name}
                            email={item.email}
                            msisdn={item.phone}
                            description="Tuition Fee"
                          />
                        ) : (
                          // Paid
                          <span className="inline-block text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                            {t("studentDash.myTutoring.paid")}
                          </span>
                        )}
                      </div>

                      <button
                        className="mt-3 text-[#2A3A54] underline inline-flex items-center gap-1"
                        onClick={() => handleToggleDetails(index)}
                      >
                        {expandedRow === index ? (
                          <>
                            <AiOutlineEyeInvisible /> {t("studentDash.myTutoring.hideDetails")}
                          </>
                        ) : (
                          <>
                            <AiOutlineEye /> {t("studentDash.myTutoring.showDetails")}
                          </>
                        )}
                      </button>

                      {expandedRow === index && (
                        <div className="mt-3 text-xs">
                          {t("studentDash.myTutoring.dayLabel")} {item.day_name || t("studentDash.myTutoring.notDefined")} | {t("studentDash.myTutoring.timingLabel")}{" "}
                          {item.start_time && item.end_time
                            ? `${formatTime(item.start_time)} to ${formatTime(
                                item.end_time
                              )}`
                            : item.timeslot_id}{" "}
                          | {t("studentDash.myTutoring.durationLabel")} {item.days} {item.duration} | {t("studentDash.myTutoring.paymentLabel")}
                          Rs.{item.payment} | {t("studentDash.myTutoring.scheduledAtLabel")}{" "}
                          {formatDateTime(item.scheduled_at)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap gap-5 justify-between items-center w-full text-sm mt-6 pb-2 px-2">
              <div className="flex items-center gap-2 text-gray-500">
                <select
                  className="text-sm text-black bg-[#F5F5F5] rounded-lg px-2 py-1"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  {[10, 20, 30, 40, 50, 100].map((per) => (
                    <option key={per} value={per}>
                      {per}
                    </option>
                  ))}
                </select>
                <span>{t("studentDash.myTutoring.showing")}</span>
                <span>
                  {totalItems === 0 ? "0-0" : `${startIndex + 1}-${endIndex}`}{" "}
                  {t("studentDash.myTutoring.entriesOf", { total: totalItems })}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="text-base text-black bg-[#F5F5F5] rounded-lg px-2 py-1"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>

                <div className="flex items-center gap-2">
                  {[...Array(Math.min(5, totalPages || 1))].map((_, idx) => {
                    const pageNumber = Math.max(currentPage - 2 + idx, 1);
                    const isActive = pageNumber === currentPage;
                    return (
                      <button
                        key={pageNumber}
                        className={
                          isActive
                            ? "text-white rounded-lg px-3 py-1 bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"
                            : "text-black rounded-lg px-3 py-1 bg-[#F5F2F5]"
                        }
                        onClick={() => handlePageChange(pageNumber)}
                        disabled={pageNumber < 1 || pageNumber > totalPages}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  <span className="text-gray-500">
                    {t("studentDash.myTutoring.pagesOf", { total: totalPages || 1 })}
                  </span>
                </div>

                <button
                  className="text-base text-black bg-[#F5F5F5] rounded-lg px-2 py-1"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCodeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white p-5 rounded-2xl shadow-2xl border border-gray-100 w-[90%] max-w-sm">
            <QRCodeDisplay classId={selectedClassId} />
            <div className="mt-4 flex justify-end">
              <button
                className="text-white px-4 py-2 rounded-md bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"
                onClick={handleCloseQRCodeModal}
              >
                {t("studentDash.common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Online Meeting Modal */}
      {showThirdModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-semibold">{t("studentDash.myTutoring.specificLink")}</h2>
                <button
                  className="text-2xl text-gray-500 hover:text-gray-700"
                  onClick={() => setShowThirdModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <p className="mb-3 text-sm text-gray-700">
                  {t("studentDash.myTutoring.hereIsSpecificLink")}
                </p>
                <div className="mb-4 p-3 bg-gray-50 border rounded-md break-all">
                  <a
                    href={specificLink}
                    className="text-[#2A3A54] underline break-all"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {specificLink}
                  </a>
                </div>
                <button
                  className="w-full text-white py-2 rounded-lg bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"
                  onClick={() => {
                    window.location.href = `/mentees/zoom-meeting-page-std?meetingUrl=${encodeURIComponent(
                      specificLink
                    )}&meetingNumber=${meetingNum}&password=${password}`;
                  }}
                >
                  {t("studentDash.myTutoring.joinMeeting")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Image Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex justify-center items-center bg-black/80">
          <div className="relative">
            <img
              src={selectedImage}
              alt={t("studentDash.myTutoring.selectedSnap")}
              className="max-w-full max-h-screen object-contain rounded-xl"
            />
            <button
              className="absolute -top-3 -right-3 text-white text-2xl bg-black/60 rounded-full h-8 w-8 grid place-items-center"
              onClick={closeImageModal}
              title={t("studentDash.common.close")}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Home class details modal */}
      {detail && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" />
          <div className="fixed inset-0 flex items-center justify-center z-50 px-3">
            <div className="bg-white rounded-2xl shadow-2xl w-[90%] sm:w-11/12 lg:w-9/12 xl:w-1/2 border border-gray-100 max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg sm:text-xl font-semibold">
                  {t("studentDash.myTutoring.studentClassesDetails")}
                </h3>
                <button
                  className="text-2xl text-gray-500 hover:text-gray-700"
                  onClick={() => setDetailModal(false)}
                >
                  ×
                </button>
              </div>

              <div className="p-4 overflow-auto">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="text-left text-xs sm:text-sm text-gray-500">
                        <th className="px-2 sm:px-4 py-3">{t("studentDash.myTutoring.colSnap")}</th>
                        <th className="px-2 sm:px-4 py-3">{t("studentDash.myTutoring.colLocation")}</th>
                        <th className="px-2 sm:px-4 py-3">{t("studentDash.myTutoring.colStartTime")}</th>
                        <th className="px-2 sm:px-4 py-3">{t("studentDash.myTutoring.colEndTime")}</th>
                        <th className="px-2 sm:px-4 py-3">{t("studentDash.myTutoring.colTotalTime")}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {studentData && studentData.length > 0 ? (
                        studentData.map((s) => {
                          const start = new Date(s.start_time);
                          const end = new Date(s.end_time);
                          const diff = Math.max(0, end - start);
                          const hrs = Math.floor(diff / 3600000);
                          const mins = Math.floor((diff % 3600000) / 60000);
                          return (
                            <tr key={s.id}>
                              <td className="px-2 sm:px-4 py-4">
                                <img
                                  src={`${apiKey}/homeSnap/${s.snap}`}
                                  alt=""
                                  className="h-12 w-12 object-cover rounded-md cursor-pointer"
                                  onClick={() =>
                                    handleImageClick(
                                      `${apiKey}/homeSnap/${s.snap}`
                                    )
                                  }
                                />
                              </td>
                              <td className="px-2 sm:px-4 py-4">
                                {s.location}
                              </td>
                              <td className="px-2 sm:px-4 py-4">
                                {s.start_time}
                              </td>
                              <td className="px-2 sm:px-4 py-4">
                                {s.end_time}
                              </td>
                              <td className="px-2 sm:px-4 py-4">
                                {hrs}h {mins}m
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-4 py-6 text-sm text-gray-500 text-center"
                          >
                            {t("studentDash.myTutoring.noDataAvailable")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default OnlineTutoring;
